import { DelayListRecordWithAction } from '../Model/DelayListRecordWithAction';
import { DelayCollectionModel } from '../Model/DelayListCollection';
import { DelayListModel } from '../Model/DelayListModel';
import { GetDelayCode } from '../services/DelayListService/DelayListService';
import { extractDatePart } from './utils';

// Type definitions for dependencies
export interface DelayListDependencies {
  dispatch: any;
  setSelectedRowId: (id: string) => void;
  handleAlert: (page: string, type: string, msg: string, id: number) => void;
  maintlist: DelayCollectionModel[];
  getSecureRandom: () => number;
}

/** Row identity shared by the table, the delay list and the saved-order snapshot. */
export const buildCompositeFlightId = (flightSegmentId: string | number, flightNumber: string): string =>
  `${flightSegmentId.toString().trim()}${flightNumber.padStart(4, "0")}`;

/**
 * Handle INSERT action for saved records
 */
export const handleSaveActionIsInsert = (
  savedRecord: DelayListRecordWithAction,
  deps: DelayListDependencies
): void => {
  const { dispatch, setSelectedRowId } = deps;

  const compositeFlightId = buildCompositeFlightId(
    savedRecord.FlightDelaySummary.FlightSegmentId,
    savedRecord.FlightDelaySummary.FlightNumber
  );

  setSelectedRowId(compositeFlightId);

  // Add the new record to the redux maintlist    
  dispatch({ type: 'ADD_MAINT_DELAY', payload: savedRecord.FlightDelaySummary });
};

/**
 * Handle UPDATE action for saved records
 */
export const handleSaveActionUpdate = (
  savedRecord: DelayListRecordWithAction,
  deps: DelayListDependencies
): void => {
  const {
    dispatch,
    setSelectedRowId,
    handleAlert,
    maintlist,
    getSecureRandom
  } = deps;

  // Get the current maintlist from Redux
  const currentMaintList = [...maintlist];

  // Find the index of the record with the same MaintDncId
  const maintListIndex = currentMaintList.findIndex(
    (item) => item.MaintDncId === savedRecord.FlightDelaySummary.MaintDncId
  );

  if (maintListIndex === -1) {
    handleAlert("Delay List", "error", "Record not found in maintlist for update.", getSecureRandom());
    return;
  }

  // Replace the existing record in place
  currentMaintList.splice(maintListIndex, 1, savedRecord.FlightDelaySummary);

  // Highlight the updated row in the delayList
  setSelectedRowId(
    buildCompositeFlightId(
      savedRecord.FlightDelaySummary.FlightSegmentId,
      savedRecord.FlightDelaySummary.FlightNumber
    )
  );

  // Dispatch the updated list to Redux
  dispatch({ type: 'UPDATE_MAINT_LIST', payload: currentMaintList });
};

/**
 * Main handler that delegates to INSERT or UPDATE actions
 */
export const handleSaveFlightDelayDetail = (
  savedRecord: DelayListRecordWithAction,
  deps: DelayListDependencies
): void => {
  if (savedRecord.Action === "INSERT") {
    handleSaveActionIsInsert(savedRecord, deps);
  } else if (savedRecord.Action === "UPDATE") {
    handleSaveActionUpdate(savedRecord, deps);
  }
};

/**
 * Transform maintlist data to DelayListModel format
 */
export const transformMaintListToDelayList = async (
  maintlist: DelayCollectionModel[],
  token: string,
  Stn: string,
  DepFlightNbr: string,
  date: string,
  h: Map<string, any>
): Promise<DelayListModel[]> => {
  return await Promise.all(
    maintlist.map(async (fltdl) => {
      let LengthOfDelayTime = fltdl.LengthOfDelay;

      const hours = Math.floor(LengthOfDelayTime / 100);
      const minutes = LengthOfDelayTime % 100;

      // Format the hours and minutes as HH:MM
      const formattedTime = `${hours
        .toString()
        .padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;

      let DCCode = fltdl.DelayCode;
      if (fltdl.DelayCode == "D1") {
        const DCCodeResult = await GetDelayCode(
          token,
          Stn,
          DepFlightNbr,
          date
        );
        if (DCCodeResult == "") {
          DCCode = fltdl.DelayCode;
        }
      } else {
        DCCode = fltdl.DelayCode;
      }
      
      h.set(
        fltdl.FlightSegmentId.toString().trim() + fltdl.FlightNumber.padStart(4, "0"),
        fltdl
      );
      
      return {
        ac: fltdl.AircraftReg,
        stn: fltdl.DelayStation,
        dptFlightNo: fltdl.FlightNumber.padStart(4, "0"),
        delayTimeLen: formattedTime,
        scheddepart:
          new Date(fltdl.DepartureScheduledTime)
            .getHours()
            .toString()
            .padStart(2, "0") +
          new Date(fltdl.DepartureScheduledTime)
            .getMinutes()
            .toString()
            .padStart(2, "0"),
        source: fltdl.ListSource,
        dcCode: DCCode,
        flightSegmentId: fltdl.FlightSegmentId.toString().trim(),
        discrepancyRemark: fltdl.DiscrepancyComments.trim(),
        comments: fltdl.MaintenanceIntComments.trim(),
        maintDncId: fltdl.MaintDncId,
        actualdepart:
          new Date(fltdl.DepartureActualTime)
            .getHours()
            .toString()
            .padStart(2, "0") +
          new Date(fltdl.DepartureActualTime)
            .getMinutes()
            .toString()
            .padStart(2, "0"),
        flightDate: extractDatePart(fltdl.FlightDate),
      };
    })
  );
};

/**
 * Transform fltopslist data to DelayListModel format
 */
export const transformFltopsListToDelayList = async (
  fltopslist: DelayCollectionModel[],
  h: Map<string, any>,
  Flt: number,
  Source: string
): Promise<DelayListModel[]> => {
  const finitialDelayList = (await (
    await Promise.all(
      fltopslist.map(async (fltdl) => {
        let LengthOfDelayTime: any = fltdl.LengthOfDelay;
        if (
          !h.has(fltdl.FlightSegmentId.toString().trim() + fltdl.FlightNumber.padStart(4, "0"))
        ) {
          h.set(
            fltdl.FlightSegmentId.toString().trim() + fltdl.FlightNumber.padStart(4, "0"),
            Flt + Source
          );
          if (LengthOfDelayTime > 59) {
            let calcLengthOfDelayHours = Math.floor(
              LengthOfDelayTime / 60
            );
            let calcLengthOfDelayMinutes =
              LengthOfDelayTime - calcLengthOfDelayHours * 60;
            LengthOfDelayTime =
              calcLengthOfDelayHours * 100 + calcLengthOfDelayMinutes;
          }
          const hours = Math.floor(LengthOfDelayTime / 100);
          const minutes = LengthOfDelayTime % 100;
          // Format the hours and minutes as HH:MM
          const formattedTime = `${hours
            .toString()
            .padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;

          return {
            ac: fltdl.AircraftReg,
            stn: fltdl.DelayStation,
            dptFlightNo: fltdl.FlightNumber.padStart(4, "0"),
            delayTimeLen: formattedTime,
            scheddepart:
              new Date(fltdl.DepartureScheduledTime)
                .getHours()
                .toString()
                .padStart(2, "0") +
              new Date(fltdl.DepartureScheduledTime)
                .getMinutes()
                .toString()
                .padStart(2, "0"),
            source: fltdl.ListSource,
            dcCode: fltdl.DelayCode,
            flightSegmentId: fltdl.FlightSegmentId.toString().trim(),
            discrepancyRemark: fltdl.DiscrepancyComments.trim(),
            comments: fltdl.MaintenanceIntComments.trim(),
            actualdepart:
              new Date(fltdl.DepartureActualTime)
                .getHours()
                .toString()
                .padStart(2, "0") +
              new Date(fltdl.DepartureActualTime)
                .getMinutes()
                .toString()
                .padStart(2, "0"),
            flightDate: extractDatePart(fltdl.FlightDate),
          };
        } else {
          return undefined;
        }
      })
    )
  ).filter((fltdl: any) => fltdl !== undefined)) as DelayListModel[];
  
  return finitialDelayList;
};
